import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import usePageMeta from '@/hooks/usePageMeta';
import { SEO_ROUTES } from '@/src/seo/routes';
import MarketingHeader from '@/components/marketing/MarketingHeader';
import MarketingFooter from '@/components/marketing/MarketingFooter';
import GettingStarted from '@/components/marketing/GettingStarted';
import { CBO_COUNT, contactHref } from '@/components/marketing/siteFacts';
import {
  ArrowRight,
  ArrowLeft,
  CheckCircle,
  AlertTriangle,
  Radio,
  Shield,
  Brain,
  Activity,
  Phone,
  Users,
  Waves,
  Siren,
  Clock,
  MapPin,
  ThermometerSun,
  Mountain,
  Wind,
  Flame,
  Droplets,
  HeartPulse,
  Satellite,
  Bell,
  MessageSquare,
  BarChart3,
  Zap,
  Target
} from 'lucide-react';

// WeaveIn Brand Colors
const BRAND = {
  purple: 'var(--v2-ink)',
  purpleDark: 'var(--v2-ink)',
  teal: 'var(--v2-accent)',
};

const API_BASE = import.meta.env.VITE_API_URL ?? '';

interface NetworkStats {
  totalProviders: number;
  totalOrganizations: number;
  statesCovered: number;
  serviceCategories: number;
}

/**
 * Emergency Response Solutions Page
 * For health plans, counties, and states — emergency communications,
 * AI-powered wave sequencing, multi-source alert polling, and safety management.
 */
export default function EmergencyResponsePage() {
  usePageMeta(SEO_ROUTES['/solutions/emergency-response']);

  const currentYear = new Date().getFullYear();
  const [stats, setStats] = useState<NetworkStats | null>(null);

  useEffect(() => {
    fetch('/provider-api/stats')
      .then(res => res.json())
      .then(data => {
        if (data.total_providers) {
          setStats({
            totalProviders: data.total_providers,
            totalOrganizations: Object.keys(data.by_organization_type || {}).length,
            statesCovered: Object.keys(data.by_state || {}).length,
            serviceCategories: 0
          });
        }
      })
      .catch(() => {
        fetch(`${API_BASE}/api/public/network-stats`)
          .then(res => res.json())
          .then(data => setStats(data))
          .catch(() => setStats(null));
      });
  }, []);

  const providerCount = stats
    ? (stats.totalProviders >= 1000000 ? (stats.totalProviders / 1000000).toFixed(1) + 'M+' : Math.floor(stats.totalProviders / 1000).toLocaleString() + 'K+')
    : '1.7M+';
  const orgCount = stats ? stats.totalOrganizations.toLocaleString() + '+' : CBO_COUNT;
  const statesCount = stats ? stats.statesCovered.toString() : '50';

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <MarketingHeader />

      {/* Hero Section */}
      <section className="bg-paper border-b border-v2border py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center px-4 py-2 bg-v2accent-soft rounded-full mb-6">
              <Siren className="w-5 h-5 text-red-400 mr-2" />
              <span className="text-ink font-medium">Emergency Response Platform</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-ink mb-6">
              When Disaster Strikes,<br />
              <span className="text-v2accent">Every Minute Counts.</span>
            </h1>
            <p className="text-xl text-ink-500 mb-4">
              AI-powered emergency communications that reach your most vulnerable populations
              through multi-wave escalation, real-time safety check-ins, and automated alert
              monitoring across 7+ federal data sources.
            </p>
            <p className="text-lg text-ink-400 mb-8">
              A use case for{' '}
              <Link to="/solutions/health-plans" className="underline hover:text-white">
                health plans and counties
              </Link>
              , county emergency services and state agencies.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link
                to={contactHref('fund', 'Emergency response demo')}
                className="inline-flex items-center px-8 py-4 bg-red-600 text-ink text-lg font-semibold rounded-lg hover:bg-red-700 transition-colors"
              >
                Request Emergency Demo
                <ArrowRight className="w-5 h-5 ml-2" />
              </Link>
              <Link
                to={contactHref('guide', 'Emergency playbook')}
                className="inline-flex items-center px-8 py-4 bg-v2accent-soft text-ink text-lg font-semibold rounded-lg hover:bg-ink/20 transition-colors"
              >
                Request the emergency playbook
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* The Problem */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              The Emergency Communication Gap
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              When wildfires, earthquakes, or public health emergencies hit, the populations
              most at risk are the hardest to reach.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mb-6">
                <Phone className="w-6 h-6 text-red-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">Unreachable Members</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Elderly, disabled, and underserved members don't respond to standard alerts.
                SMS alone reaches less than 40% of at-risk populations during emergencies.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                One-channel outreach leaves the most vulnerable behind
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mb-6">
                <Clock className="w-6 h-6 text-orange-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">Delayed Response</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                Manual alert monitoring and campaign creation takes hours. By the time
                communications go out, members are already in danger.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Hours of delay = lives at risk
              </div>
            </div>

            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-6 h-6 text-v2accent-ink" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-3">No Safety Visibility</h3>
              <p className="text-gray-600 dark:text-gray-300 mb-4">
                After sending alerts, you have no idea who's safe, who needs help,
                or who needs immediate welfare checks.
              </p>
              <div className="text-sm text-gray-500 dark:text-gray-400">
                Blind spot on member safety status
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Alert Source Monitoring */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Real-Time Alert Source Polling
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              WeaveIn continuously monitors 7+ federal and state data sources, automatically
              matching alerts to your member populations by geography.
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-12">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center border-2 border-green-200 dark:border-green-800">
              <div className="w-14 h-14 bg-green-100 dark:bg-green-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Satellite className="w-7 h-7 text-green-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">FEMA</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Disaster Declarations</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs font-medium text-green-700 dark:text-green-400">Connected</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center border-2 border-green-200 dark:border-green-800">
              <div className="w-14 h-14 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Wind className="w-7 h-7 text-blue-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">NWS</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Severe Weather Alerts</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs font-medium text-green-700 dark:text-green-400">Connected</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center border-2 border-green-200 dark:border-green-800">
              <div className="w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Mountain className="w-7 h-7 text-orange-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">USGS</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Earthquake Alerts (M3.5+)</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs font-medium text-green-700 dark:text-green-400">Connected</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center border-2 border-green-200 dark:border-green-800">
              <div className="w-14 h-14 bg-v2accent-soft rounded-xl flex items-center justify-center mx-auto mb-4">
                <ThermometerSun className="w-7 h-7 text-v2accent-ink" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">EPA AirNow</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Air Quality Index</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs font-medium text-green-700 dark:text-green-400">Connected</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Flame className="w-7 h-7 text-red-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">NIFC Wildfire</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Active fires &gt;100 acres</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-green-100 dark:bg-green-900/30 rounded text-xs font-medium text-green-700 dark:text-green-400">Connected</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-cyan-100 dark:bg-cyan-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <Droplets className="w-7 h-7 text-cyan-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">USGS Flood</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Stream gauge monitoring</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded text-xs font-medium text-yellow-700 dark:text-yellow-400">Planned</div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <div className="w-14 h-14 bg-pink-100 dark:bg-pink-900/30 rounded-xl flex items-center justify-center mx-auto mb-4">
                <HeartPulse className="w-7 h-7 text-pink-600" />
              </div>
              <h4 className="font-bold text-ink dark:text-white mb-1">CDC HAN</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Health Advisory Network</p>
              <div className="mt-2 inline-flex items-center px-2 py-1 bg-yellow-100 dark:bg-yellow-900/30 rounded text-xs font-medium text-yellow-700 dark:text-yellow-400">Planned</div>
            </div>
          </div>

          <div className="mt-12 bg-v2accent rounded-2xl p-8 text-white">
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <div>
                <div className="text-3xl font-bold text-v2accent mb-1">Automatic</div>
                <div className="text-gray-300">Geographic member matching against every alert</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-v2accent mb-1">Human-in-the-Loop</div>
                <div className="text-gray-300">Alerts require approval before campaigns launch</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-v2accent mb-1">Deduplication</div>
                <div className="text-gray-300">No duplicate alerts or repeated member contacts</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Communication Waves */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              AI-Powered Communication Waves
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
              Intelligent multi-wave escalation ensures no member is left behind.
              Each wave targets non-responders with an escalating channel strategy.
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {/* Wave 1 */}
            <div className="relative">
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border-t-4 border-v2accent h-full">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-v2accent-soft rounded-xl flex items-center justify-center">
                    <MessageSquare className="w-7 h-7 text-v2accent" />
                  </div>
                  <div className="ml-4">
                    <div className="text-v2accent text-sm font-bold">WAVE 1</div>
                    <h3 className="text-xl font-bold text-ink dark:text-white">Bulk SMS & WhatsApp</h3>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Immediate mass notification to all targeted members via SMS and WhatsApp
                  with personalized safety check-in prompts.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Personalized with member name & location
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    110+ language support
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Reply "SAFE" / "HELP" / "SHELTER" keywords
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-v2accent mr-2 mt-0.5 flex-shrink-0" />
                    Bypasses marketing quiet hours
                  </li>
                </ul>
              </div>
            </div>

            {/* Wave 2 */}
            <div className="relative">
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border-t-4 border-orange-500 h-full">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-orange-100 dark:bg-orange-900/30 rounded-xl flex items-center justify-center">
                    <Phone className="w-7 h-7 text-orange-600" />
                  </div>
                  <div className="ml-4">
                    <div className="text-orange-500 text-sm font-bold">WAVE 2</div>
                    <h3 className="text-xl font-bold text-ink dark:text-white">Voice AI Calls</h3>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Non-responders receive automated voice calls using Google TTS with
                  natural-sounding voice presets and IVR safety check-in.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    Google Text-to-Speech via Twilio
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    Press 1 = Safe, 2 = Need Help, 3 = Shelter
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    Configurable delay (4h, 8h, 1d after Wave 1)
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-orange-500 mr-2 mt-0.5 flex-shrink-0" />
                    Retry logic for unanswered calls
                  </li>
                </ul>
              </div>
            </div>

            {/* Wave 3 */}
            <div className="relative">
              <div className="bg-white dark:bg-gray-900 rounded-2xl p-8 shadow-lg border-t-4 border-red-600 h-full">
                <div className="flex items-center mb-6">
                  <div className="w-14 h-14 bg-red-100 dark:bg-red-900/30 rounded-xl flex items-center justify-center">
                    <Users className="w-7 h-7 text-red-600" />
                  </div>
                  <div className="ml-4">
                    <div className="text-red-600 text-sm font-bold">WAVE 3</div>
                    <h3 className="text-xl font-bold text-ink dark:text-white">Case Manager Tasks</h3>
                  </div>
                </div>
                <p className="text-gray-600 dark:text-gray-300 mb-6">
                  Still-unreachable members trigger urgent welfare check tasks assigned
                  to case managers with 2-hour SLA.
                </p>
                <ul className="space-y-3 text-sm text-gray-600 dark:text-gray-400">
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    Auto-created urgent tasks
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    Member contact info + incident context
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    Prioritized by risk
                  </li>
                  <li className="flex items-start">
                    <CheckCircle className="w-4 h-4 text-red-600 mr-2 mt-0.5 flex-shrink-0" />
                    Full audit trail for compliance
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* AI Safety Management */}
      <section className="py-20 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl font-bold text-ink dark:text-white mb-6">
                AI-Driven Safety Management & Sequencing
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 mb-8">
                Outreach is sequenced so the highest-risk members are reached first,
                prioritized by risk, and automatically escalating "HELP" responses
                to urgent welfare checks.
              </p>

              <div className="space-y-6">
                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Brain className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Risk Prioritization</h4>
                    <p className="text-gray-600 dark:text-gray-300">Non-responders prioritized by risk. Hardest-to-reach members get attention first.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Zap className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Auto-Escalation Rules</h4>
                    <p className="text-gray-600 dark:text-gray-300">"HELP" responses instantly create urgent tasks with 2-hour deadlines for case managers.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Target className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">SDOH-Aware Audience Segmentation</h4>
                    <p className="text-gray-600 dark:text-gray-300">Filter by geography, risk level, health conditions, SDOH factors, and engagement score.</p>
                  </div>
                </div>

                <div className="flex items-start">
                  <div className="w-12 h-12 bg-v2accent rounded-xl flex items-center justify-center mr-4 flex-shrink-0">
                    <Activity className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-bold text-ink dark:text-white">Real-Time Safety Dashboard</h4>
                    <p className="text-gray-600 dark:text-gray-300">Live feed of safety responses with hourly trending, response rates, and unreachable counts.</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-gray-50 dark:bg-gray-800 rounded-2xl p-8">
              <h3 className="text-xl font-bold text-ink dark:text-white mb-6">
                Safety Check-In Response Types
              </h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-green-500 rounded-full mr-3"></div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">SAFE</span>
                  </div>
                  <span className="text-sm text-gray-500">"safe", "ok", "im safe", Press 1</span>
                </div>
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-red-500 rounded-full mr-3"></div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">NEEDS HELP</span>
                  </div>
                  <span className="text-sm text-gray-500">"help", "emergency", Press 2</span>
                </div>
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-orange-500 rounded-full mr-3"></div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">SHELTER</span>
                  </div>
                  <span className="text-sm text-gray-500">"shelter", "evacuating", Press 3</span>
                </div>
                <div className="flex items-center justify-between bg-white dark:bg-gray-900 rounded-lg p-4">
                  <div className="flex items-center">
                    <div className="w-3 h-3 bg-blue-500 rounded-full mr-3"></div>
                    <span className="font-medium text-gray-700 dark:text-gray-300">EVACUATED</span>
                  </div>
                  <span className="text-sm text-gray-500">"evacuated", "left", "relocated"</span>
                </div>
              </div>

              <div className="mt-6 p-4 bg-red-50 dark:bg-red-900/20 rounded-lg">
                <div className="flex items-start">
                  <AlertTriangle className="w-5 h-5 text-red-600 mr-3 mt-0.5 flex-shrink-0" />
                  <div>
                    <div className="font-semibold text-red-700 dark:text-red-400 text-sm">Auto-Escalation</div>
                    <p className="text-sm text-red-600 dark:text-red-300 mt-1">
                      "NEEDS HELP" responses automatically create urgent welfare check tasks
                      with a 2-hour deadline assigned to the nearest case manager.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Use Cases by Audience */}
      <section className="py-16 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Built for Emergency Stakeholders
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            {/* Health Plans */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-v2accent-soft rounded-xl flex items-center justify-center mb-6">
                <Shield className="w-6 h-6 text-v2accent-ink" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-4">For Health Plans</h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Reach Medicaid/Medicare members during disasters
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Safety check-ins for chronic condition members
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Emergency consent tier bypasses opt-outs
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Compliance audit trail for regulators
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Connect members to {providerCount} community providers
                </li>
              </ul>
            </div>

            {/* Counties */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-blue-100 dark:bg-blue-900/30 rounded-xl flex items-center justify-center mb-6">
                <MapPin className="w-6 h-6 text-blue-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-4">For Counties</h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  County-wide emergency communications
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  ZIP-code level geographic targeting
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Coordinate across {orgCount} service categories
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Real-time shelter & evacuation tracking
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Multi-language outreach (110+ languages)
                </li>
              </ul>
            </div>

            {/* States */}
            <div className="bg-white dark:bg-gray-900 rounded-xl p-8 shadow-lg">
              <div className="w-12 h-12 bg-teal-100 dark:bg-teal-900/30 rounded-xl flex items-center justify-center mb-6">
                <BarChart3 className="w-6 h-6 text-teal-600" />
              </div>
              <h3 className="text-xl font-bold text-ink dark:text-white mb-4">For State Agencies</h3>
              <ul className="space-y-3 text-gray-600 dark:text-gray-300">
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  State-wide disaster response coordination
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  FEMA region and multi-county targeting
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Aggregate safety status across populations
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Integration with existing emergency systems
                </li>
                <li className="flex items-start">
                  <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-0.5 flex-shrink-0" />
                  Provider network across {statesCount} states
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Incident Types */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Emergency Types We Support
            </h2>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <Flame className="w-10 h-10 text-red-500 mx-auto mb-4" />
              <h4 className="font-bold text-ink dark:text-white mb-2">Natural Disasters</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Wildfires, earthquakes, hurricanes, floods, tornadoes, winter storms</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <HeartPulse className="w-10 h-10 text-pink-500 mx-auto mb-4" />
              <h4 className="font-bold text-ink dark:text-white mb-2">Public Health</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Disease outbreaks, air quality emergencies, water contamination</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <AlertTriangle className="w-10 h-10 text-orange-500 mx-auto mb-4" />
              <h4 className="font-bold text-ink dark:text-white mb-2">Facility Emergencies</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Hospital evacuations, power outages, hazmat incidents</p>
            </div>
            <div className="bg-gray-50 dark:bg-gray-800 rounded-xl p-6 text-center">
              <Siren className="w-10 h-10 text-red-600 mx-auto mb-4" />
              <h4 className="font-bold text-ink dark:text-white mb-2">Mass Casualty Events</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">Coordinated response with rapid member welfare verification</p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="py-20 bg-gray-50 dark:bg-gray-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              From Alert to Safety in Minutes
            </h2>
          </div>

          <div className="grid lg:grid-cols-4 gap-6">
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg relative">
              <div className="w-10 h-10 bg-v2accent rounded-full flex items-center justify-center text-white font-bold mb-4">1</div>
              <h4 className="font-bold text-ink dark:text-white mb-2">Alert Detected</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                System automatically polls FEMA, NWS, USGS, and other sources. New alerts matched to member geography.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg relative">
              <div className="w-10 h-10 bg-v2accent rounded-full flex items-center justify-center text-white font-bold mb-4">2</div>
              <h4 className="font-bold text-ink dark:text-white mb-2">Incident Created</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Staff reviews alert, approves incident creation, and configures audience targeting and wave schedule.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg relative">
              <div className="w-10 h-10 bg-v2accent rounded-full flex items-center justify-center text-white font-bold mb-4">3</div>
              <h4 className="font-bold text-ink dark:text-white mb-2">Waves Execute</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                SMS, Voice AI, and case manager tasks deploy in sequence. Each wave targets non-responders from the previous.
              </p>
            </div>
            <div className="bg-white dark:bg-gray-900 rounded-xl p-6 shadow-lg relative">
              <div className="w-10 h-10 bg-v2accent rounded-full flex items-center justify-center text-white font-bold mb-4">4</div>
              <h4 className="font-bold text-ink dark:text-white mb-2">Safety Confirmed</h4>
              <p className="text-sm text-gray-600 dark:text-gray-300">
                Real-time dashboard shows safe, needs help, shelter, and evacuated counts. Urgent cases auto-escalated.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Network Stats */}
      <section className="py-16 bg-white dark:bg-gray-900">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-ink dark:text-white mb-4">
              Backed by the Trust Network
            </h2>
          </div>

          <div className="grid md:grid-cols-4 gap-8 mb-12">
            <div className="text-center">
              <div className="text-5xl font-bold text-ink dark:text-white mb-2">{providerCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Providers in Network</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-ink dark:text-white mb-2">{orgCount}</div>
              <div className="text-gray-600 dark:text-gray-400">Service Categories</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-ink dark:text-white mb-2">7+</div>
              <div className="text-gray-600 dark:text-gray-400">Federal Alert Sources</div>
            </div>
            <div className="text-center">
              <div className="text-5xl font-bold text-v2accent mb-2">110+</div>
              <div className="text-gray-600 dark:text-gray-400">Languages Supported</div>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
            <div className="text-center bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <div className="text-2xl font-bold text-ink dark:text-white mb-1">5 Channels</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">SMS, Voice AI, WhatsApp, Email, Case Manager</div>
            </div>
            <div className="text-center bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <div className="text-2xl font-bold text-ink dark:text-white mb-1">4 Severity Levels</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Advisory, Watch, Warning, Emergency</div>
            </div>
            <div className="text-center bg-gray-50 dark:bg-gray-800 rounded-xl p-6">
              <div className="text-2xl font-bold text-ink dark:text-white mb-1">HIPAA Compliant</div>
              <div className="text-sm text-gray-600 dark:text-gray-400">Full audit trail, consent-aware</div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-paper border-b border-v2border">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-ink mb-6">
            Don't Wait for the Next Emergency.
          </h2>
          <p className="text-xl text-ink-500 mb-10">
            See how WeaveIn's emergency response platform can protect your most vulnerable
            populations with AI-powered communication waves and real-time safety tracking.
          </p>
          <div className="flex flex-col sm:flex-row justify-center items-center gap-4">
            <Link
              to={contactHref('fund', 'Emergency response demo')}
              className="inline-flex items-center px-8 py-4 bg-red-600 text-ink text-lg font-semibold rounded-lg hover:bg-red-700 transition-colors shadow-lg"
            >
              Request Emergency Demo
              <ArrowRight className="w-5 h-5 ml-2" />
            </Link>
            <Link
              to={contactHref('guide', 'Emergency playbook')}
              className="inline-flex items-center px-8 py-4 bg-transparent text-ink text-lg font-semibold rounded-lg border-2 border-ink hover:bg-ink hover:text-paper transition-colors"
            >
              Request the emergency playbook
            </Link>
          </div>
        </div>
      </section>

      <GettingStarted />

      {/* Footer */}
      <MarketingFooter />
    </div>
  );
}
